import React, { Fragment, useEffect, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import CheckIcon from "@mui/icons-material/Check";
import Navbar from "./components/navbar";
import Footer from "./components/Footer";
import { BASE_URL } from "./apiConfig";
import { auth } from "./firebase";
import { tenantHeaders } from "./lib/tenantConfig";
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import { useNavigate } from "react-router-dom";
import { Alert } from "./components/ui/alert";
import ManageUsersModal from "./components/ManageUsersModal";
 
interface UserItem {
  id: string;
  name: string | null;
  mobile_number: string | null;
  location: string | null;
  email: string;
  role: string;
  status: string;
  created_on: string;
  updated_on: string;
  updated_by: string;
}

interface Counts {
  total_users: number;
  active_users: number;
  inactive_users: number;
}

// const LIMIT_OPTIONS = [5, 10, 20, 50, 100];

interface FilterState {
  role: string;
  status: string;
  created: string;
  updated: string;
}

interface TenantItem {
  id: number;
  slug: string;
  name: string;
  custom_domain: string | null;
  created_at: string | null;
}

interface EditModalProps {
  user: UserItem | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (updatedUser: UserItem) => void;
  setAlertMsg: (msg: string | null) => void;
  setAlertType: (type: "success" | "destructive" | "info" | "warning" | "primary") => void;
  tenants: TenantItem[];
}

const UserAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState<UserItem[]>([]);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  // const [error, setError] = useState<string | null>(null);
  const [alertMsg, setAlertMsg] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<"success" | "destructive" | "info" | "warning" | "primary">("info");

  const [search, setSearch] = useState<string>("");
  const [filters, setFilters] = useState<FilterState>({
    role: '',
    status: '',
    created: '',
    updated: ''
  });

  const [limit, setLimit] = useState<number>(10);
  const [pageIndex, setPageIndex] = useState<number>(0);

  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"users" | "tenants">("users");
  const [tenants, setTenants] = useState<TenantItem[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState<TenantItem | null>(null);

  // Guard: only admin and corporate_admin can access this page
  useEffect(() => {
    const role = localStorage.getItem("userRole");
    if (role !== "admin" && role !== "corporate_admin") {
      navigate("/");
    }
  }, [navigate]);

  // Fetch all users once
  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const url = `${BASE_URL}/user/list?req_user_id=${encodeURIComponent(
        auth.currentUser?.uid || localStorage.getItem("uid") || "kSePzpzTAnUYxdmpsqH882utawc2"
      )}`;
      const res = await fetch(url, { headers: tenantHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.status !== "success") throw new Error("API error");
      setAllUsers(json.data.users || []);
      setCounts(json.data.counts || null);
    } catch (e: any) {
      console.error("Error fetching users:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await Promise.all([fetchAllUsers(), fetchTenants()]);
      setInitialLoading(false);
    })();
  }, []);

  const fetchTenants = async () => {
    setTenantsLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/tenant/list`);
      const json = await res.json();
      if (json.status === "success") setTenants(json.data || []);
    } catch (e) {
      console.error("Error fetching tenants:", e);
    } finally {
      setTenantsLoading(false);
    }
  };

  const handleSaveTenant = async (data: { name: string; slug: string; custom_domain: string }, tenantId?: number) => {
    try {
      if (tenantId) {
        const res = await fetch(`${BASE_URL}/tenant/${tenantId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: data.name, custom_domain: data.custom_domain || null }),
        });
        const json = await res.json();
        if (json.status !== "success") throw new Error(json.detail || "Update failed");
        setAlertType("success");
        setAlertMsg("Tenant updated successfully.");
      } else {
        const res = await fetch(`${BASE_URL}/tenant/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: data.name, slug: data.slug, custom_domain: data.custom_domain || null }),
        });
        const json = await res.json();
        if (json.status !== "success") throw new Error(json.detail || "Create failed");
        setAlertType("success");
        setAlertMsg("Tenant created successfully.");
      }
      await fetchTenants();
      setShowTenantModal(false);
      setEditingTenant(null);
    } catch (e: any) {
      setAlertType("destructive");
      setAlertMsg(e.message || "Failed to save tenant.");
    }
  };

  // Filtering and pagination (client-side)
  const filteredUsers = React.useMemo(() => {
    let filtered = allUsers;
    if (search.trim()) {
      filtered = filtered.filter(u =>
        u.email.toLowerCase().includes(search.trim().toLowerCase())
      );
    }
    if (filters.role) filtered = filtered.filter(u => u.role === filters.role);
    if (filters.status) filtered = filtered.filter(u => u.status === filters.status);
    if (filters.created) filtered = filtered.filter(u => u.created_on.slice(0, 10) === filters.created);
    if (filters.updated) filtered = filtered.filter(u => u.updated_on.slice(0, 10) === filters.updated);
    return filtered;
  }, [allUsers, search, filters]);

  // Pagination math
  const total = filteredUsers.length;
  const pageCount = Math.max(1, Math.ceil(total / limit));
  useEffect(() => {
    if (pageIndex >= pageCount) setPageIndex(pageCount - 1);
  }, [pageCount, pageIndex]);
  const startIndex = pageIndex * limit;
  const endIndex = Math.min(startIndex + limit, total);
  const users = filteredUsers.slice(startIndex, endIndex);

  // Reset pageIndex when filters/search/limit change
  useEffect(() => {
    setPageIndex(0);
  }, [search, filters, limit]);

  // Pagination controls
  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLimit(Number(e.target.value));
  };
  const goToPage = (idx: number) => setPageIndex(Math.max(0, Math.min(idx, pageCount - 1)));

  const openEdit = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setEditingUser(user);
      setIsEditModalOpen(true);
    }
  };

  // Avatar initials
  // const getInitials = (email: string) => {
  //   if (!email) return "U";
  //   const parts = email.split("@")[0].split(".");
  //   if (parts.length === 1) return parts[0][0].toUpperCase();
  //   return parts.map(p => p[0].toUpperCase()).join("").slice(0, 2);
  // };

  const [editingUser, setEditingUser] = useState<UserItem | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Update handleUserUpdate to refetch users
  const handleUserUpdate = async (updatedUser: UserItem) => {
    setAllUsers(allUsers.map(u => u.id === updatedUser.id ? updatedUser : u));
    setPageIndex(0);
    await fetchAllUsers(); // <-- Refetch users after update
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const [resetLoadingId, setResetLoadingId] = useState<string | null>(null);
  const [deleteLoadingId, setDeleteLoadingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleDeleteUser = async (userId: string) => {
    setDeleteLoadingId(userId);
    try {
      const res = await fetch(`${BASE_URL}/user/delete/${userId}`, {
        method: "DELETE",
        headers: tenantHeaders(),
      });
      const json = await res.json();
      if (json.status === "success") {
        setAlertType("success");
        setAlertMsg("User deleted successfully.");
        await fetchAllUsers();
      } else {
        throw new Error(json.detail || "Delete failed");
      }
    } catch (e: any) {
      setAlertType("destructive");
      setAlertMsg(e.message || "Failed to delete user.");
    } finally {
      setDeleteLoadingId(null);
      setConfirmDeleteId(null);
    }
  };

  const handlePasswordReset = async (email: string, userId: string) => {
    setResetLoadingId(userId);
    try {
      const response = await fetch(`${BASE_URL}/user/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...tenantHeaders() },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Failed to send reset link');
      }

      const result = await response.json();
      if (result.status === 'success') {
        setAlertType("success");
        setAlertMsg("Password reset link sent successfully.");
      } else {
        throw new Error(result.message || 'Failed to send reset link');
      }
    } catch (err: any) {
      setAlertType("destructive");
      setAlertMsg(err.message || "Failed to send reset link");
    } finally {
      setResetLoadingId(null);
    }
  };

  // Add this useEffect to auto-dismiss alerts
  useEffect(() => {
    if (alertMsg) {
      const timer = setTimeout(() => {
        setAlertMsg(null);
      }, 5000); // 5 seconds

      // Cleanup timeout on component unmount or when alertMsg changes
      return () => clearTimeout(timer);
    }
  }, [alertMsg]);
  
  const isOverlayOpen = isEditModalOpen || isAddUserModalOpen;

  // First, add a function to reset filters in the UserAnalytics component
  const resetFilters = () => {
    setSearch('');
    setFilters({
      role: '',
      status: '',
      created: '',
      updated: ''
    });
    setLimit(10);
    setPageIndex(0);
  };

  return (
    <div
      className="min-h-screen relative flex flex-col overflow-x-hidden discover-page"
      style={{
        background:
          "radial-gradient(35% 19% at 50% 0%, rgba(255, 84, 31, 0.5) 0%, rgba(0, 0, 0, 0) 100%), black",
        color: "white",
      }}
    >
      {/* Mobile-only tweaks for dots and glow */}
      <style>{`
        @media (max-width: 640px) {
          .discover-page .bg-dots { display: none !important; }
          .discover-page .bg-conic { display: none !important; }

          /* Mobile typography adjustments */
          .page-title { font-size: 28px !important; line-height: 36px !important; }
          .page-subtitle { font-size: 14px !important; line-height: 20px !important; }
          .users-header { padding: 12px 16px !important; }
        }
      `}</style>

      {/* Dots background layer */}
      <div
        className="absolute bg-dots"
        style={{
          width: "1106px",
          height: "500px",
          top: "-12px",
          left: "194px",
          backgroundImage: "url('/dots.png')",
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          opacity: 1,
          pointerEvents: "none",
          userSelect: "none",
          zIndex: 0,
        }}
      />

      {/* Designer conic-gradient glow layer */}
      <div
        className="fixed bg-conic pointer-events-none"
        style={{
          width: "500px",
          height: "503px",
          bottom: "-220px",   // was: top: "1071px"
          right: "-220px",    // was: left: "1064px"
          opacity: 1,
          background:
            "conic-gradient(from 180deg at 50% 50%, #FB1E1E 0deg, #FA5424 84.77deg, #C22C00 183.8deg, #982300 271.91deg, #FB1E1E 360deg)",
          filter: "blur(400px)",
          userSelect: "none",
          zIndex: 0,
        }}
      />

      {/* Top Nav with subtle divider like reference */}
      {!isOverlayOpen && (
        <div className="w-full bg-transparent border-b border-white/10 shadow-sm relative z-[1000]">
          <Navbar />
        </div>
      )}

      {alertMsg && (
        <Alert
          variant={alertType}
          close
          onClose={() => setAlertMsg(null)}
        >
          {alertMsg}
        </Alert>
      )}

      {/* Apply reference-like margins and max width */}
      <main className="relative z-0 flex-1 w-full max-w-[1440px] mx-auto px-4 md:px-12 lg:px-24 pb-16 mt-12">
        {initialLoading ? (
          <div className="flex flex-col items-center justify-center gap-4 py-32">
            <svg className="animate-spin h-10 w-10 text-orange-400" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-gray-400 text-sm">Loading Users...</p>
          </div>
        ) : (
        <>
        {/* Centered Page Header */}
        <div className="flex flex-col items-center justify-center text-center gap-2 mt-10 mb-10 relative">
          <button
            onClick={() => navigate("/")}
            className="absolute left-0 top-0 flex items-center gap-1.5 text-sm text-gray-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/10 transition-all border border-white/10"
          >
            ← Back to Chat
          </button>
          <h1
            className="page-title"
            style={{
              fontFamily: "IBM Plex Sans, sans-serif",
              fontWeight: 700,
              fontStyle: "normal",
              fontSize: "30px",
              lineHeight: "70.4px",
              letterSpacing: "0%",
              textAlign: "center",
              color: "rgba(255, 255, 255, 1)",
            }}
          >
            User Analytics Dashboard
          </h1>
          <p
            className="page-subtitle"
            style={{
              fontFamily: "Lora, serif",
              fontWeight: 400,
              fontStyle: "normal",
              fontSize: "18px",
              lineHeight: "21.6px",
              letterSpacing: "0%",
              textAlign: "center",
              color: "rgba(255, 255, 255, 0.85)",
            }}
          >
            Comprehensive overview of platform users and their activities
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex items-center gap-2 mb-8 border-b border-white/10">
          {(["users", "tenants"] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-2.5 text-sm font-medium capitalize transition-all border-b-2 -mb-px ${
                activeTab === tab
                  ? "border-[#FF541F] text-[#FF541F]"
                  : "border-transparent text-gray-400 hover:text-white"
              }`}
            >
              {tab === "users" ? "Users" : "Tenants"}
            </button>
          ))}
        </div>

        {activeTab === "users" && (<>
        {/* KPI Strip (reference-like) */}
        <div className="w-full mb-10">
          <div
            className="rounded-[28px] border border-white/10 bg-white/10 supports-[backdrop-filter]:backdrop-blur-xl backdrop-blur-xl"
            style={{
              // Remove old border styles, use glassy effect like navbar
              // borderWidth: "0px",
              // borderStyle: "solid",
              // borderColor: "rgba(240, 240, 240, 1)"
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 auto-rows-fr divide-y md:divide-y-0 md:divide-x divide-white/10">
              <KpiStat
                label="TOTAL USERS"
                value={counts?.total_users ?? (loading ? "..." : 0)}
                icon={<img src="/total_ active_users.png" alt="Total Users" className="w-14 h-14" />}
                // trend={{ dir: "up", delta: 11 }}
              />
              <KpiStat
                label="ACTIVE USERS"
                value={counts?.active_users ?? (loading ? "..." : 0)}
                icon={<img src="/active_users.png" alt="Active Users" className="w-14 h-14" />}
                // trend={{ dir: "down", delta: 6 }}
              />
              <KpiStat
                label="DEACTIVATED USERS"
                value={counts?.inactive_users ?? (loading ? "..." : 0)}
                icon={<img src="/inactive users.png" alt="DEACTIVATED Users" className="w-14 h-14" />}
              />
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div className="px-6 py-5 users-header border-b border-gray-700/50 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <h2
              className="text-xl font-semibold text-white flex items-center gap-3"
              style={{
                fontFamily: "IBM Plex Sans, sans-serif",
                fontWeight: 500,
                fontStyle: "normal",
                fontSize: "26px",
                lineHeight: "24px",
                letterSpacing: "-1%",
                color: "rgba(235, 235, 235, 1)",
              }}
            >
              <span>Users Directory</span>
            </h2>
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <button
              onClick={() => setIsAddUserModalOpen(true)}
              className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-[#FF541F] to-[#FF6A3A] text-white font-medium text-lg shadow hover:from-orange-600 hover:to-orange-400 transition-all w-full md:w-auto"
              style={{
                fontFamily: "Lora, serif",
                fontWeight: 500,
                fontSize: "16px",
                lineHeight: "20px",
                letterSpacing: "0%",
              }}
            >
              <span style={{ fontSize: "22px", fontWeight: 700, marginRight: "4px" }}>+</span>
              Add User
            </button>
            {(search || filters.role || filters.status || filters.created || filters.updated) && (
              <Tooltip title="Reset Filters" arrow>
                <IconButton
                  onClick={resetFilters}
                  sx={{
                    background: 'linear-gradient(to right, #FF541F, #FF6A3A)',
                    color: '#FFFFFF',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    minWidth: '48px',
                    height: '41px',
                    '&:hover': {
                      background: 'linear-gradient(to right, #ff4100, #ff5a2a)',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '24px',
                    }
                  }}
                  size="large"
                  aria-label="Reset Filters"
                  className="ml-auto md:ml-0"
                >
                  <RestartAltIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Search + Role Filter */}
        <div className="flex items-center gap-3 my-4 flex-wrap">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by email..."
            className="flex-1 min-w-[200px] px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-orange-500/50"
          />
          {(() => {
            const ROLE_OPTIONS = [
              { value: "", label: "All Roles" },
              { value: "admin", label: "Admin" },
              { value: "staff", label: "Staff" },
              { value: "corporate_admin", label: "Corporate Admin" },
              { value: "ma", label: "M&A Team" },
              { value: "finance", label: "Finance Team" },
              { value: "strategy", label: "Strategy Team" },
              { value: "viewer", label: "Viewer" },
              { value: "user", label: "General User" },
            ];
            const selectedRole = ROLE_OPTIONS.find(r => r.value === filters.role) || ROLE_OPTIONS[0];
            return (
              <Listbox value={filters.role} onChange={(v: string) => setFilters(f => ({ ...f, role: v }))}>
                {({ open }) => (
                  <div className={`relative ${open ? "z-[200]" : "z-10"}`}>
                    <Listbox.Button className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-gray-300 text-sm focus:outline-none flex items-center gap-2 min-w-[140px] justify-between">
                      <span>{selectedRole.label}</span>
                      <svg width="10" height="6" viewBox="0 0 12 8" fill="none" aria-hidden="true">
                        <path d="M1 2l5 4 5-4" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </Listbox.Button>
                    <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                      <Listbox.Options className="absolute mt-1 min-w-[180px] max-h-72 overflow-auto bg-[#1a1a1e] border border-white/10 rounded-lg shadow-lg">
                        {ROLE_OPTIONS.map(opt => (
                          <Listbox.Option
                            key={opt.value}
                            value={opt.value}
                            className={({ active }) =>
                              `cursor-pointer select-none relative py-2 pl-4 pr-9 text-sm ${active ? "bg-white/10 text-white" : "text-gray-300"}`
                            }
                          >
                            {({ selected }) => (
                              <>
                                <span>{opt.label}</span>
                                {selected && (
                                  <span className="absolute inset-y-0 right-3 flex items-center text-orange-400">
                                    <CheckIcon style={{ fontSize: 18 }} />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                )}
              </Listbox>
            );
          })()}
        </div>

          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full">
              <thead className="bg-white/5">
  <tr>
    <th className="p-4 text-left text-gray-400 font-medium">Email</th>
    <th className="p-4 text-left text-gray-400 font-medium">Role</th>
    <th className="p-4 text-left text-gray-400 font-medium">Status</th>
    <th className="p-4 text-left text-gray-400 font-medium">Actions</th>
  </tr>
</thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={4} className="p-8 text-center">
                      <div className="flex items-center justify-center gap-2 text-gray-400">
                        <svg className="animate-spin h-5 w-5 text-orange-400" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Loading users...
                      </div>
                    </td>
                  </tr>
                )}
                {users.length === 0 && !loading && (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-gray-400">No users found</td>
                  </tr>
                )}
                {!loading && users.map((u) => (
                  <tr key={u.id} className="border-t border-white/5 hover:bg-white/5">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-400 font-semibold text-sm flex-shrink-0">
                          {u.email[0].toUpperCase()}
                        </div>
                        <span className="text-white text-sm truncate max-w-xs">{u.email}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 text-xs rounded-full bg-white/10 text-gray-300">
                        {{"admin":"Admin","staff":"Staff","corporate_admin":"Corporate Admin","ma":"M&A Team","finance":"Finance Team","strategy":"Strategy Team","viewer":"Viewer","user":"General User"}[u.role] || u.role}
                      </span>
                    </td>
                    <td className="p-4">
                      {u.status === "inactive"
                        ? <span className="px-2 py-1 text-xs rounded-full bg-red-500/20 text-red-400">Inactive</span>
                        : <span className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-400">Active</span>
                      }
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEdit(u.id)} className="p-2 rounded-lg hover:bg-white/10 transition" title="Edit">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} stroke="#9ca3af" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                        <button onClick={() => handlePasswordReset(u.email, u.id)} disabled={resetLoadingId === u.id} className="p-2 rounded-lg hover:bg-white/10 transition" title="Reset Password">
                          {resetLoadingId === u.id
                            ? <span className="text-xs text-gray-400">...</span>
                            : <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} stroke="#60a5fa" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
                          }
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(u.id)}
                          disabled={deleteLoadingId === u.id}
                          className="p-2 rounded-lg hover:bg-red-500/10 transition"
                          title="Delete User"
                        >
                          {deleteLoadingId === u.id
                            ? <span className="text-xs text-gray-400">...</span>
                            : <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} stroke="#f87171" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          }
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Update Pagination */}
          <div
  className="px-6 py-4 border-t border-gray-700/50"
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end", // Align everything to right
    gap: "24px",
    minHeight: "64px",
  }}
>
    {/* Pagination buttons - left side */}
    <div className="flex items-center gap-2 mr-8">
      <button
        disabled={pageIndex === 0 || loading}
        onClick={() => goToPage(pageIndex - 1)}
        className={`w-8 h-8 flex items-center justify-center rounded-md border border-gray-600 text-white transition-all ${
          pageIndex === 0 || loading
            ? "bg-gray-800/50 text-gray-500 cursor-not-allowed"
            : "bg-transparent hover:bg-orange-500/80 hover:text-white"
        }`}
        style={{ fontWeight: 600 }}
      >
        &lt;
      </button>
      {Array.from({ length: pageCount }).map((_, idx) => (
        <button
          key={idx}
          onClick={() => goToPage(idx)}
          className={`w-8 h-8 flex items-center justify-center rounded-md border border-gray-600 mx-0.5 text-white transition-all ${
            idx === pageIndex
              ? "bg-[#FF541F] text-white font-bold border-orange-500"
              : "bg-transparent hover:bg-orange-500/80 hover:text-white"
          }`}
          style={{
            fontWeight: idx === pageIndex ? 700 : 500,
            fontSize: "16px",
          }}
        >
          {idx + 1}
        </button>
      ))}
      <button
        disabled={pageIndex === pageCount - 1 || loading}
        onClick={() => goToPage(pageIndex + 1)}
        className={`w-8 h-8 flex items-center justify-center rounded-md border border-gray-600 text-white transition-all ${
          pageIndex === pageCount - 1 || loading
            ? "bg-gray-800/50 text-gray-500 cursor-not-allowed"
            : "bg-transparent hover:bg-orange-500/80 hover:text-white"
        }`}
        style={{ fontWeight: 600 }}
      >
        &gt;
      </button>
    </div>
    {/* Items/page dropdown - right side */}
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-400">Showing</span>
      <select
        value={limit}
        onChange={handleLimitChange}
        className="px-3 py-1 rounded-md text-sm font-semibold bg-[#FF541F] text-white border-none focus:ring-2 focus:ring-orange-400"
        style={{
          minWidth: "56px",
          height: "32px",
          fontSize: "16px",
          fontWeight: 500,
          boxShadow: "0 2px 8px rgba(255,84,31,0.12)",
        }}
      >
        {[5, 10, 20, 50, 100].map((value) => (
          <option key={value} value={value}>{value}</option>
        ))}
      </select>
      <span className="text-sm text-gray-400">items/page</span>
    </div>
  </div>
        </>)}

        {/* ─── Tenants Tab ──────────────────────────────────────────────────── */}
        {activeTab === "tenants" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 style={{ fontFamily: "IBM Plex Sans, sans-serif", fontWeight: 500, fontSize: "26px", color: "rgba(235,235,235,1)" }}>
                Tenant Directory
              </h2>
              <button
                onClick={() => { setEditingTenant(null); setShowTenantModal(true); }}
                className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-[#FF541F] to-[#FF6A3A] text-white font-medium shadow hover:from-orange-600 hover:to-orange-400 transition-all"
              >
                <span className="text-xl font-bold">+</span> Add Tenant
              </button>
            </div>

            {/* Tenant KPI */}
            <div className="w-full mb-8">
              <div className="rounded-[28px] border border-white/10 bg-white/10 backdrop-blur-xl">
                <div className="flex items-center gap-4 px-6 py-6">
                  <div className="w-14 h-14 rounded-full bg-[#FF541F] flex items-center justify-center text-white text-2xl font-bold">
                    {tenantsLoading ? "…" : tenants.length}
                  </div>
                  <div>
                    <span className="text-xs font-medium uppercase tracking-wider text-white/70">TOTAL TENANTS</span>
                    <div className="text-2xl font-semibold text-white mt-1">{tenantsLoading ? "Loading..." : `${tenants.length} Corporate Org${tenants.length !== 1 ? "s" : ""}`}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tenants Table */}
            <div className="rounded-xl border border-white/10 overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="p-4 text-left text-gray-400 font-medium">Organisation</th>
                    <th className="p-4 text-left text-gray-400 font-medium">Slug</th>
                    <th className="p-4 text-left text-gray-400 font-medium">URL</th>
                    <th className="p-4 text-left text-gray-400 font-medium">Custom Domain</th>
                    <th className="p-4 text-left text-gray-400 font-medium">Created</th>
                    <th className="p-4 text-left text-gray-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tenantsLoading && (
                    <tr><td colSpan={6} className="p-8 text-center text-gray-400">Loading...</td></tr>
                  )}
                  {!tenantsLoading && tenants.length === 0 && (
                    <tr><td colSpan={6} className="p-8 text-center text-gray-400">No tenants yet. Click "Add Tenant" to create one.</td></tr>
                  )}
                  {tenants.map(t => (
                    <tr key={t.id} className="border-t border-white/5 hover:bg-white/5">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-400 font-semibold text-sm">
                            {t.name[0].toUpperCase()}
                          </div>
                          <span className="text-white font-medium">{t.name}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="px-2 py-1 text-xs rounded-full bg-white/10 text-gray-300 font-mono">{t.slug}</span>
                      </td>
                      <td className="p-4 text-blue-400 text-sm font-mono">{t.slug}.bigdatamatica.com</td>
                      <td className="p-4 text-gray-400 text-sm">{t.custom_domain || <span className="text-gray-600">—</span>}</td>
                      <td className="p-4 text-gray-400 text-sm">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</td>
                      <td className="p-4">
                        <button
                          onClick={() => { setEditingTenant(t); setShowTenantModal(true); }}
                          className="p-2 rounded-lg hover:bg-white/10 transition" title="Edit"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} stroke="#9ca3af" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>
        )}

      </main>

      {/* Tenant Modal */}
      {showTenantModal && (
        <TenantModal
          tenant={editingTenant}
          onClose={() => { setShowTenantModal(false); setEditingTenant(null); }}
          onSave={handleSaveTenant}
        />
      )}

      {/* Footer with spacing like reference */}
      <div className="mt-auto w-full">
        <Footer />
      </div>

      {/* Edit User Modal */}
      <EditUserModal
        user={editingUser}
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setEditingUser(null); }}
        onUpdate={handleUserUpdate}
        setAlertMsg={setAlertMsg}
        setAlertType={setAlertType}
        tenants={tenants}
      />

      {/* Add User Modal */}
      <ManageUsersModal
        isOpen={isAddUserModalOpen}
        onClose={() => {
          setIsAddUserModalOpen(false);
          fetchAllUsers(); // Refetch users after adding
        }}
      />

      {/* Confirm Delete Dialog */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="w-full max-w-sm rounded-2xl bg-[#0f1117] border border-white/10 shadow-2xl p-7 flex flex-col items-center gap-5">
            <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} stroke="#f87171" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </div>
            <h3 className="text-lg font-bold text-white text-center">Delete User?</h3>
            <p className="text-gray-400 text-sm text-center">
              This will permanently remove the user from Firebase and the database. This action cannot be undone.
            </p>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="flex-1 py-2.5 rounded-lg text-white font-medium border border-white/20 hover:bg-white/5 transition text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteUser(confirmDeleteId)}
                disabled={deleteLoadingId === confirmDeleteId}
                className="flex-1 py-2.5 rounded-lg text-white font-medium transition text-sm"
                style={{ background: "linear-gradient(0deg, #ef4444, #ef4444)" }}
              >
                {deleteLoadingId === confirmDeleteId ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// const RoleBadge: React.FC<{ role: string }> = ({ role }) => {
//   const style = role === "admin"
//     ? "bg-violet-50 text-violet-700 border-violet-200"
//     : "bg-indigo-50 text-indigo-700 border-indigo-200";
    
//   return (
//     <span className={`inline-flex items-center px-2.5 py-1.5 rounded-lg text-xs font-semibold ${style} border`}>
//       {role.charAt(0).toUpperCase() + role.slice(1)}
//     </span>
//   );
// };

const EditUserModal: React.FC<EditModalProps> = ({
  user,
  isOpen,
  onClose,
  onUpdate,
  setAlertMsg,
  setAlertType,
  tenants
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile_number: '',
    location: '',
    role: '',
    status: '',
    tenant_id: '',
  });

  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name ?? '',
        email: user.email,
        mobile_number: user.mobile_number ?? '',
        location: user.location ?? '',
        role: user.role,
        status: user.status,
        tenant_id: '',
      });
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${BASE_URL}/user/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...tenantHeaders() },
        body: JSON.stringify({
          requested_by: auth.currentUser?.uid || localStorage.getItem("uid"),
          user_id: user.id,
          name: formData.name?.trim() || null,
          email: formData.email,
          mobile_number: formData.mobile_number?.trim() || null,
          location: formData.location?.trim() || null,
          role: formData.role,
          status: formData.status,
          tenant_id: formData.tenant_id ? Number(formData.tenant_id) : null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update user');
      }

      const result = await response.json();
      if (result.status === 'success') {
        onUpdate({ ...user, ...formData });
        onClose();
        setAlertType("success");
        setAlertMsg("User updated successfully.");
      } else {
        throw new Error(result.message || 'Update failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
      setAlertType("destructive");
      setAlertMsg(err.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value;
    if (formData.status === "active" && newStatus === "inactive") {
      setPendingStatus(newStatus);
      setShowConfirm(true);
    } else {
      setFormData(prev => ({ ...prev, status: newStatus }));
    }
  };

  const handleConfirmDeactivate = () => {
    setFormData(prev => ({ ...prev, status: pendingStatus! }));
    setShowConfirm(false);
    setPendingStatus(null);
  };

  const handleCancelDeactivate = () => {
    setShowConfirm(false);
    setPendingStatus(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>

        {/* Modal */}
        <div className="relative w-full max-w-md rounded-2xl bg-black border border-gray-700/50 shadow-2xl">
          <div className="px-6 py-4 border-b border-gray-700/50">
            <h3
              style={{
                fontFamily: "Lora, serif",
                fontWeight: 600,
                fontStyle: "normal",
                fontSize: "24px",
                lineHeight: "20px",
                letterSpacing: "0%",
                verticalAlign: "middle",
                color: "rgba(237, 238, 240, 1)",
              }}
            >
              Edit Users
            </h3>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && (
              <Alert variant="destructive" close onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Email (read-only) */}
            <div className="space-y-2">
              <label className="block" style={{ fontFamily: "Lora, serif", fontWeight: 500, fontSize: "14px", color: "rgba(237, 238, 240, 1)" }}>
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                readOnly
                className="w-full px-3 py-2 rounded-lg border border-gray-600 bg-[#23242a] text-gray-400 cursor-not-allowed"
                style={{ fontFamily: "Lora, serif", fontWeight: 500, fontSize: "14px" }}
              />
            </div>

            {/* Role */}
            <div className="space-y-2">
              <label
                className="block"
                style={{
                  fontFamily: "Lora, serif",
                  fontWeight: 500,
                  fontStyle: "normal",
                  fontSize: "14px",
                  lineHeight: "20px",
                  letterSpacing: "0%",
                  verticalAlign: "middle",
                  color: "rgba(237, 238, 240, 1)",
                }}
              >
                User Type
              </label>
              <select
                value={formData.role}
                onChange={e => setFormData(prev => ({ ...prev, role: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-600 bg-[#23242a] text-white focus:border-orange-500"
                required
                style={{
                  fontFamily: "Lora, serif",
                  fontWeight: 500,
                  fontStyle: "normal",
                  fontSize: "14px",
                  lineHeight: "20px",
                  letterSpacing: "0%",
                  verticalAlign: "middle",
                  color: "rgba(237, 238, 240, 1)",
                }}
              >
                <option value="admin">Admin</option>
                <option value="staff">Staff</option>
                <option value="corporate_admin">Corporate Admin</option>
                <option value="ma">M&A Team</option>
                <option value="finance">Finance Team</option>
                <option value="strategy">Strategy Team</option>
                <option value="viewer">Viewer</option>
                <option value="user">General User</option>
              </select>
            </div>

            {/* Tenant Assignment */}
            {tenants.length > 0 && (
              <div className="space-y-2">
                <label className="block" style={{ fontFamily: "Lora, serif", fontWeight: 500, fontSize: "14px", color: "rgba(237,238,240,1)" }}>
                  Assign to Tenant
                </label>
                <select
                  value={formData.tenant_id ?? ""}
                  onChange={e => setFormData(prev => ({ ...prev, tenant_id: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-600 bg-[#23242a] text-white focus:border-orange-500"
                  style={{ fontFamily: "Lora, serif", fontSize: "14px" }}
                >
                  <option value="">— No Tenant —</option>
                  {tenants.map(t => (
                    <option key={t.id} value={String(t.id)}>{t.name} · {t.slug}.bigdatamatica.com</option>
                  ))}
                </select>
              </div>
            )}

            {/* Status */}
            {formData.status !== "pending" && (
  <div className="space-y-2">
    <label
      className="block"
      style={{
        fontFamily: "Lora, serif",
        fontWeight: 500,
        fontStyle: "normal",
        fontSize: "14px",
        lineHeight: "20px",
        letterSpacing: "0%",
        verticalAlign: "middle",
        color: "rgba(237, 238, 240, 1)",
      }}
    >
      Status
    </label>
    <select
      value={formData.status}
      onChange={handleStatusChange}
      className="w-full px-3 py-2 rounded-lg border border-gray-600 bg-[#23242a] text-white focus:border-orange-500"
      required
      style={{
        fontFamily: "Lora, serif",
        fontWeight: 500,
        fontStyle: "normal",
        fontSize: "14px",
        lineHeight: "20px",
        letterSpacing: "0%",
        verticalAlign: "middle",
        color: "rgba(237, 238, 240, 1)",
      }}
    >
      {formData.status === "active" ? (
        <>
          <option value="active">Active</option>
          <option value="inactive">Deactivate</option>
        </>
      ) : (
        <>
          <option value="inactive">Deactivated</option>
          <option value="active">Activate</option>
        </>
      )}
    </select>
  </div>
)}
            <div className="flex items-center justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-orange-500 border border-orange-500 rounded-lg bg-transparent hover:bg-orange-500/10 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-[#FF541F] to-[#FF6A3A] hover:from-orange-600 hover:to-orange-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading && (
                  <svg className="w-4 h-4 mr-2 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                )}
                Update User
              </button>
            </div>
          </form>

          {/* Confirmation dialog for deactivation */}
          {showConfirm && (
  <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/60">
    <div className="bg-[#23242a] rounded-xl shadow-xl border border-gray-700/50 p-6 max-w-sm w-full">
      <h4 className="text-lg font-semibold mb-2" style={{ color: "#fff", fontFamily: "Lora, serif" }}>
        Confirm Deactivation
      </h4>
      <p className="mb-4 text-gray-300" style={{ fontFamily: "Lora, serif" }}>
        Are you sure you want to deactivate this user? They will lose access to the platform.
      </p>
      <div className="flex justify-end gap-3">
        <button
          onClick={handleCancelDeactivate}
          className="px-4 py-2 text-sm font-medium text-gray-300 border border-gray-500 rounded-lg bg-transparent hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleConfirmDeactivate}
          className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#FF541F] to-[#FF6A3A] rounded-lg hover:from-orange-600 hover:to-orange-400 transition-all"
        >
          Yes, Deactivate
        </button>
      </div>
    </div>
  </div>
)}
        </div>
      </div>
    </div>
  );
};

// ─── Tenant Modal ─────────────────────────────────────────────────────────────
const TenantModal: React.FC<{
  tenant: TenantItem | null;
  onClose: () => void;
  onSave: (data: { name: string; slug: string; custom_domain: string }, id?: number) => void;
}> = ({ tenant, onClose, onSave }) => {
  const isEdit = !!tenant;
  const [name, setName] = useState(tenant?.name || "");
  const [slug, setSlug] = useState(tenant?.slug || "");
  const [customDomain, setCustomDomain] = useState(tenant?.custom_domain || "");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || (!isEdit && !slug.trim())) return;
    setSaving(true);
    await onSave({ name: name.trim(), slug: slug.trim(), custom_domain: customDomain.trim() }, tenant?.id);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-md rounded-2xl bg-black border border-gray-700/50 shadow-2xl">
          <div className="px-6 py-4 border-b border-gray-700/50">
            <h3 style={{ fontFamily: "Lora, serif", fontWeight: 600, fontSize: "22px", color: "rgba(237,238,240,1)" }}>
              {isEdit ? "Edit Tenant" : "Add New Tenant"}
            </h3>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="space-y-2">
              <label className="block text-sm text-gray-300">Organisation Name <span className="text-orange-400">*</span></label>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                required
                placeholder="e.g. Infosys"
                className="w-full px-3 py-2 rounded-lg border border-gray-600 bg-[#23242a] text-white focus:border-orange-500 focus:outline-none"
              />
            </div>

            {!isEdit ? (
              <div className="space-y-2">
                <label className="block text-sm text-gray-300">
                  Slug <span className="text-orange-400">*</span>
                  <span className="text-gray-500 text-xs ml-2">lowercase letters, numbers, hyphens only</span>
                </label>
                <input
                  value={slug}
                  onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  required
                  placeholder="e.g. infosys"
                  className="w-full px-3 py-2 rounded-lg border border-gray-600 bg-[#23242a] text-white focus:border-orange-500 focus:outline-none font-mono"
                />
                {slug && (
                  <p className="text-xs text-gray-500">Login URL → <span className="text-blue-400 font-mono">{slug}.bigdatamatica.com</span></p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <label className="block text-sm text-gray-300">Slug <span className="text-gray-500 text-xs">(cannot be changed)</span></label>
                <input value={tenant.slug} readOnly className="w-full px-3 py-2 rounded-lg border border-gray-600 bg-[#1a1a1e] text-gray-500 font-mono cursor-not-allowed" />
                <p className="text-xs text-gray-500">Login URL → <span className="text-blue-400 font-mono">{tenant.slug}.bigdatamatica.com</span></p>
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm text-gray-300">Custom Domain <span className="text-gray-500 text-xs">(optional)</span></label>
              <input
                value={customDomain}
                onChange={e => setCustomDomain(e.target.value)}
                placeholder="e.g. portal.infosys.com"
                className="w-full px-3 py-2 rounded-lg border border-gray-600 bg-[#23242a] text-white focus:border-orange-500 focus:outline-none"
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-orange-500 border border-orange-500 rounded-lg hover:bg-orange-500/10 transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-[#FF541F] to-[#FF6A3A] hover:from-orange-600 hover:to-orange-400 disabled:opacity-50 transition-all">
                {saving ? "Saving..." : isEdit ? "Update Tenant" : "Create Tenant"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

/* Updated KpiStat component to match the image */
const KpiStat: React.FC<{
  label: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: { dir: "up" | "down" | "flat"; delta?: number };
}> = ({ label, value, icon, trend }) => {
  const trendColor =
    trend?.dir === "up" ? "text-emerald-400" : trend?.dir === "down" ? "text-rose-400" : "text-gray-400";

  return (
    <div className="flex items-center gap-4 px-6 py-6">
      <div className="w-14 h-14 rounded-full bg-[#FF541F] text-white flex items-center justify-center">
        {icon}
      </div>
      <div className="flex flex-col">
        <span className="text-xs font-medium uppercase tracking-wider text-white/70">{label}</span>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-2xl md:text-3xl font-semibold text-white">{value}</span>
          {trend && (
            <span className={`inline-flex items-center gap-1 text-xs font-medium ${trendColor}`}>
              {trend.dir === "up" && (
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13 7h7v7h-2V9.414l-7.293 7.293-4-4L2 18.414 3.414 20l3.293-3.293 4 4L20.586 11H17z" />
                </svg>
              )}
              {trend.dir === "down" && (
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11 17H4V10h2v4.586l7.293-7.293 4 4L22 5.586 20.586 4l-3.293 3.293-4-4L3.414 13H7z" />
                </svg>
              )}
              <span>{trend.delta}% this month</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserAnalytics;